const requireId = (id, label) => {
  if (id == null) {
    throw new Error(`appointmentKeys.${label}: expected a finite id, got ${String(id)}`);
  }
  const n = Number(id);
  if (!Number.isFinite(n)) {
    throw new Error(`appointmentKeys.${label}: expected a finite id, got ${String(id)}`);
  }
  return n;
};

const toEpoch = (d) => (d instanceof Date ? d.getTime() : new Date(d).getTime());

/**
 * @typedef {Object} CalendarRangeArgs
 * @property {Date | string | number} from
 * @property {Date | string | number} to
 * @property {'day' | 'week' | 'month' | 'agenda' | string} [view]
 * @property {Record<string, unknown>} [filters]
 *
 * @typedef {Object} QueueListArgs
 * @property {number} [limit]
 * @property {string} [sort]
 * @property {number} [page]
 * @property {Record<string, unknown>} [filters]
 */

const root = 'appointments';

export const appointmentKeys = {
  all: [root],

  // ─── Detail (single appointment) ───────────────────────────────────────────
  // `detail` keys v1 (`/api/appointment/get_one`) responses.
  // `detailV2` keys v2 (`/api/appointment/get_one_v2`) — the two endpoints
  // return different shapes, so the cache slots stay separate until the v1
  // hook is retired. Do not collapse without migrating v1 consumers first.
  details: () => [root, 'detail'],
  detail: (id) => [root, 'detail', requireId(id, 'detail')],
  detailsV2: () => [root, 'detailV2'],
  detailV2: (id) => [root, 'detailV2', requireId(id, 'detailV2')],

  // ─── Calendar (date-range, view-scoped) ────────────────────────────────────
  // Range bounds are normalized to epoch ms so overlap predicates are
  // integer compares — no Date parsing in the predicate hot path.
  calendar: () => [root, 'calendar'],
  calendarRange: ({ from, to, view, filters }) => [
    root,
    'calendar',
    { fromMs: toEpoch(from), toMs: toEpoch(to), view, filters }
  ],

  // ─── Patient-scoped lists ──────────────────────────────────────────────────
  patientsAll: () => [root, 'patient'],
  byPatient: (patientId) => [root, 'patient', requireId(patientId, 'byPatient')],
  // Prefix that matches every filter/type variant of a patient's list queries.
  byPatientListAll: (patientId) => [
    root,
    'patient',
    requireId(patientId, 'byPatientListAll'),
    'list'
  ],
  byPatientList: (patientId, type, filters) => [
    root,
    'patient',
    requireId(patientId, 'byPatientList'),
    'list',
    type,
    filters
  ],
  byPatientOverview: (patientId) => [
    root,
    'patient',
    requireId(patientId, 'byPatientOverview'),
    'overview'
  ],
  // Prefix that matches every active/inactive variant of a patient's infinite queries.
  byPatientInfiniteAll: (patientId) => [
    root,
    'patient',
    requireId(patientId, 'byPatientInfiniteAll'),
    'infinite'
  ],
  byPatientInfinite: (patientId, active) => [
    root,
    'patient',
    requireId(patientId, 'byPatientInfinite'),
    'infinite',
    active
  ],

  // ─── Queue / inbox ─────────────────────────────────────────────────────────
  queue: () => [root, 'queue'],
  queueList: ({ limit, sort, page, filters, pageCategory, dateRange }) => [
    root,
    'queue',
    {
      limit,
      sort,
      page,
      filters,
      pageCategory,
      dateFromMs: dateRange?.startDate == null ? null : toEpoch(dateRange.startDate),
      dateToMs: dateRange?.endDate == null ? null : toEpoch(dateRange.endDate)
    }
  ],
  pending: () => [root, 'pending'],

  // ─── Per-appointment sub-resources ─────────────────────────────────────────
  // The plural form is the prefix (matches every id under that resource).
  timelines: () => [root, 'timeline'],
  timeline: (id) => [root, 'timeline', requireId(id, 'timeline')],
  // ExpandableToolbar activity log — outside the appointments root by design.
  activities: (id) => ['activities', 'appointment', requireId(id, 'activities')],
  invoicesAll: () => [root, 'invoices'],
  invoices: (id) => [root, 'invoices', requireId(id, 'invoices')],
  receipts: (id) => [root, 'receipts', requireId(id, 'receipts')]
};

export const __testables = { requireId, toEpoch };
